/**
 * Admin-only endpoint slice — `/api/v1/alerts*` and `/api/v1/alert-rules*`.
 *
 * Mirrors `apps/api-worker/src/routes/alerts.ts` EXACTLY: the DTOs are the
 * `c.json()` shapes, the enums are the zod enums, and there is a wrapper here
 * if and only if a handler exists there. Nothing is stubbed or invented.
 *
 * Built app-locally on the raw client (see the note on `apiClient` in
 * `../api.js`) so every call still goes through the refresh lock, idempotency
 * keys and typed error mapping.
 */
import type { RequestOptions } from '@sonauto/api-client';
import { apiClient } from '../api.js';

/** Options a caller may add to any wrapper (abort signal, reason, …). */
type Extra = Pick<RequestOptions, 'signal' | 'reason' | 'idempotencyKey' | 'timeoutMs'>;

function extra(o: Extra | undefined): RequestOptions {
  return o === undefined ? {} : { ...o };
}

// ---------------------------------------------------------------------------
// Enums — copied verbatim from the route module's zod schemas
// ---------------------------------------------------------------------------

export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'HIGH', 'CRITICAL'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_CATEGORIES = [
  'OPERATION',
  'FINANCE',
  'INVENTORY',
  'HR',
  'SECURITY',
  'FRAUD',
  'SYSTEM',
  'TRANSLATION',
  'CUSTOMER',
] as const;
export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

/** The alert types the queue-consumer evaluator actually implements. */
export const ALERT_TYPES = [
  'KITCHEN_OVERLOAD',
  'ITEM_WAITING_TOO_LONG',
  'CASH_VARIANCE',
  'DISCOUNT_EXCEEDED',
  'CANCEL_AFTER_PRODUCTION',
  'INVOICE_REPRINTED',
  'NEGATIVE_STOCK',
  'SOLD_OUT_ITEM_SOLD',
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export const ALERT_STATUSES = [
  'OPEN',
  'ACKNOWLEDGED',
  'IN_PROGRESS',
  'ESCALATED',
  'RESOLVED',
  'IGNORED',
] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

/** The statuses the API treats as "still open" (`status=OPEN_ANY`). */
export const OPEN_ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'IN_PROGRESS', 'ESCALATED'] as const;

export const ALERT_RESOLUTION_TYPES = [
  'FIXED',
  'FALSE_POSITIVE',
  'ACCEPTED_RISK',
  'DUPLICATE',
  'ESCALATED',
  'EXPIRED',
] as const;
export type AlertResolutionType = (typeof ALERT_RESOLUTION_TYPES)[number];

export const RULE_COMPARATORS = ['GT', 'GTE', 'LT', 'LTE', 'EQ', 'NEQ', 'BETWEEN'] as const;
export type RuleComparator = (typeof RULE_COMPARATORS)[number];

// ---------------------------------------------------------------------------
// DTOs — the `mapAlert` / `mapRule` shapes
// ---------------------------------------------------------------------------

export interface Alert {
  readonly id: string;
  readonly branchId: string | null;
  readonly alertRuleId: string | null;
  readonly alertType: string;
  readonly category: AlertCategory;
  readonly severity: AlertSeverity;
  readonly titleVi: string;
  readonly messageVi: string | null;
  readonly resourceType: string | null;
  readonly resourceId: string | null;
  readonly metricValueMilli: number | null;
  readonly thresholdValueMilli: number | null;
  readonly status: AlertStatus;
  readonly assigneeUserId: string | null;
  readonly assignedAt: number | null;
  readonly acknowledgedBy: string | null;
  readonly acknowledgedAt: number | null;
  readonly resolvedBy: string | null;
  readonly resolvedAt: number | null;
  readonly resolutionType: string | null;
  readonly resolutionNote: string | null;
  readonly escalationLevel: number;
  readonly occurrenceCount: number;
  readonly firstDetectedAt: number;
  readonly lastDetectedAt: number;
  readonly rowVersion: number;
  readonly createdAt: number;
}

/** The detail route additionally parses `evidence_json`. */
export interface AlertDetail extends Alert {
  readonly evidence: unknown;
}

export interface AlertTimelineEntry {
  readonly id: string;
  readonly action: string;
  readonly actorUserId: string | null;
  readonly note: string | null;
  readonly detail: unknown;
  readonly createdAt: number;
}

export interface AlertDetailResponse {
  readonly alert: AlertDetail;
  readonly timeline: readonly AlertTimelineEntry[];
}

export interface AlertListResponse {
  readonly alerts: readonly Alert[];
  readonly limit: number;
  readonly offset: number;
}

/** `GET /alerts/summary` — counts over the OPEN statuses only. */
export interface AlertSummaryResponse {
  readonly bySeverity: Readonly<Record<string, number>>;
  readonly byCategory: Readonly<Record<string, number>>;
  readonly openTotal: number;
}

export interface AlertRule {
  readonly id: string;
  readonly branchId: string | null;
  readonly code: string;
  readonly nameVi: string;
  readonly descriptionVi: string | null;
  readonly alertType: string;
  readonly category: AlertCategory;
  readonly severity: AlertSeverity;
  readonly condition: unknown;
  readonly windowSeconds: number;
  readonly thresholdValueMilli: number | null;
  readonly comparison: string;
  readonly cooldownSeconds: number;
  readonly autoResolveSeconds: number | null;
  readonly notifyRoleCodes: unknown;
  readonly notifyPermissionCode: string | null;
  readonly escalateAfterSeconds: number | null;
  readonly escalateToRoleCode: string | null;
  readonly requiresResolution: boolean;
  readonly isActive: boolean;
  readonly version: number;
  /** Platform default rules (tenant NULL) are read-only templates. */
  readonly editable: boolean;
  readonly rowVersion: number;
  readonly createdAt: number;
  readonly updatedAt: number;
}

// ---------------------------------------------------------------------------
// Request bodies — mirror the zod schemas
// ---------------------------------------------------------------------------

export interface AlertResolveInput {
  readonly resolutionType: AlertResolutionType;
  readonly note?: string | null;
}

/** `ruleBodySchema` — condition tree editing is deliberately not surfaced yet. */
export interface AlertRuleInput {
  readonly code: string;
  readonly nameVi: string;
  readonly descriptionVi?: string | null;
  readonly alertType: AlertType;
  readonly category?: AlertCategory;
  readonly severity?: AlertSeverity;
  readonly branchId?: string | null;
  readonly windowSeconds?: number;
  readonly thresholdValueMilli?: number | null;
  readonly comparison?: RuleComparator;
  readonly cooldownSeconds?: number;
  readonly autoResolveSeconds?: number | null;
  readonly notifyRoleCodes?: readonly string[];
  readonly notifyPermissionCode?: string | null;
  readonly escalateAfterSeconds?: number | null;
  readonly escalateToRoleCode?: string | null;
  readonly requiresResolution?: boolean;
  readonly isActive?: boolean;
}

/** `rulePatchSchema` — every field optional plus the optimistic-lock version. */
export interface AlertRulePatch extends Partial<AlertRuleInput> {
  readonly rowVersion: number;
}

export interface AlertListQuery {
  readonly branchId?: string;
  readonly category?: AlertCategory;
  readonly severity?: AlertSeverity;
  /** A concrete status, or `OPEN_ANY` for every still-open status. */
  readonly status?: AlertStatus | 'OPEN_ANY';
  /** Epoch millis, inclusive, on `created_at`. */
  readonly from?: number;
  readonly to?: number;
  readonly limit?: number;
  readonly offset?: number;
}

// ---------------------------------------------------------------------------
// Wrappers
// ---------------------------------------------------------------------------

export const alertsApi = {
  /** GET /v1/alerts — permission `alert.view`. */
  list: (query?: AlertListQuery, o?: Extra): Promise<AlertListResponse> =>
    apiClient.get<AlertListResponse>('/v1/alerts', { ...extra(o), query: { ...query } }),

  /** GET /v1/alerts/summary — permission `alert.view`. */
  summary: (query?: { branchId?: string }, o?: Extra): Promise<AlertSummaryResponse> =>
    apiClient.get<AlertSummaryResponse>('/v1/alerts/summary', { ...extra(o), query: query ?? {} }),

  /** GET /v1/alerts/:id — detail + the `alert_actions` timeline. */
  detail: (id: string, o?: Extra): Promise<AlertDetailResponse> =>
    apiClient.get<AlertDetailResponse>(`/v1/alerts/${encodeURIComponent(id)}`, extra(o)),

  /** POST /v1/alerts/:id/acknowledge — "I saw it"; permission `alert.view`. */
  acknowledge: (id: string, o?: Extra): Promise<{ ok: boolean; alertId: string; status: AlertStatus }> =>
    apiClient.post<{ ok: boolean; alertId: string; status: AlertStatus }>(
      `/v1/alerts/${encodeURIComponent(id)}/acknowledge`,
      extra(o),
    ),

  /** POST /v1/alerts/:id/assign — permission `alert.resolve`. */
  assign: (
    id: string,
    body: { assigneeUserId: string },
    o?: Extra,
  ): Promise<{ ok: boolean; alertId: string; status: AlertStatus; assigneeUserId: string }> =>
    apiClient.post<{ ok: boolean; alertId: string; status: AlertStatus; assigneeUserId: string }>(
      `/v1/alerts/${encodeURIComponent(id)}/assign`,
      { ...extra(o), body },
    ),

  /**
   * POST /v1/alerts/:id/resolve — permission `alert.resolve`.
   * The API REJECTS this without `x-action-reason`; pass `reason` through
   * `headerReason()` so Vietnamese text survives the header encoding.
   */
  resolve: (
    id: string,
    body: AlertResolveInput,
    reason: string,
    o?: Extra,
  ): Promise<{ ok: boolean; alertId: string; status: AlertStatus; resolutionType: AlertResolutionType }> =>
    apiClient.post<{ ok: boolean; alertId: string; status: AlertStatus; resolutionType: AlertResolutionType }>(
      `/v1/alerts/${encodeURIComponent(id)}/resolve`,
      { ...extra(o), body, reason },
    ),

  /** POST /v1/alerts/:id/ignore — reason REQUIRED; permission `alert.resolve`. */
  ignore: (id: string, reason: string, o?: Extra): Promise<{ ok: boolean; alertId: string; status: AlertStatus }> =>
    apiClient.post<{ ok: boolean; alertId: string; status: AlertStatus }>(
      `/v1/alerts/${encodeURIComponent(id)}/ignore`,
      { ...extra(o), reason },
    ),

  // --- rules ---------------------------------------------------------------

  /** GET /v1/alert-rules — tenant rules + inherited platform defaults; permission `alert.manage`. */
  listRules: (
    query?: { branchId?: string; alertType?: AlertType },
    o?: Extra,
  ): Promise<{ rules: readonly AlertRule[] }> =>
    apiClient.get<{ rules: readonly AlertRule[] }>('/v1/alert-rules', { ...extra(o), query: { ...query } }),

  /** POST /v1/alert-rules — permission `alert.manage`. */
  createRule: (body: AlertRuleInput, o?: Extra): Promise<{ rule: AlertRule }> =>
    apiClient.post<{ rule: AlertRule }>('/v1/alert-rules', { ...extra(o), body }),

  /** PUT /v1/alert-rules/:id — optimistic-locked; permission `alert.manage`. */
  updateRule: (id: string, body: AlertRulePatch, o?: Extra): Promise<{ rule: AlertRule }> =>
    apiClient.put<{ rule: AlertRule }>(`/v1/alert-rules/${encodeURIComponent(id)}`, { ...extra(o), body }),

  /** POST /v1/alert-rules/:id/toggle — permission `alert.manage`. */
  toggleRule: (id: string, o?: Extra): Promise<{ ok: boolean; ruleId: string; isActive: boolean }> =>
    apiClient.post<{ ok: boolean; ruleId: string; isActive: boolean }>(
      `/v1/alert-rules/${encodeURIComponent(id)}/toggle`,
      extra(o),
    ),
};
